import type {NormalizedCacheObject} from '@apollo/client';
import type {Mapper} from '@automapper/core';
import type {CustomErrorCodesLocalizationKeys} from '@error-handling';
import type {ApolloClientProxy, IApolloClientBuilder, RestService} from '@igp/shared/api';
import {ApolloClientGraphQlTransportWsBuilder, ApolloClientInMemoryCache, RestFetchApiService, RestXhrService} from '@igp/shared/api';
import type {IUserManagerExtended} from '@igp/shared/auth';
import {UserManagerExtended} from '@igp/shared/auth';
import {AgentProfileService} from '@igp/shared/features/agent-profile';
import {BackpackService} from '@igp/shared/features/backpack';
import {BoUserGroupService, boUserGroupServiceTypes} from '@igp/shared/features/bo-user-group';
import {BonusCodeService} from '@igp/shared/features/bonus-code';
import type {
    IBulkApplyStrategy,
    IBulkStrategy,
    PerformStrategyOperationResponse,
    PerformStrategyRequest,
    ValidationStrategyResponse,
} from '@igp/shared/features/bulk-operation';
import {BulkOperationService} from '@igp/shared/features/bulk-operation';
import {CountriesService} from '@igp/shared/features/country';
import {PlayerBonusService} from '@igp/shared/features/player-bonus';
import {type IPlayerReferralUpdateService, PlayerReferralEpicsBuilder, PlayerReferralService} from '@igp/shared/features/player-referral';
import type {
    ApplyP2PTransfersRequest,
    ITransactionService,
    P2PTransferModel,
    ValidationP2PTransferRequest,
} from '@igp/shared/features/transaction';
import {
    ApplyP2PTransfersStrategy,
    LoadP2PTransferStrategy,
    LoadP2PTransferViaFileStrategy,
    P2PTransferTemplateFactory,
    PerformP2PTransfersStrategy,
    TransactionService,
    ValidationP2PTransferStrategy,
} from '@igp/shared/features/transaction';
import {UserActivitySummaryService} from '@igp/shared/features/user-activity-summary';
import type {IUserProfileService, UserProfileViewModelKeys} from '@igp/shared/features/user-profile';
import {UserProfileEpicsBuilder, UserProfileService} from '@igp/shared/features/user-profile';
import type {IEntityFileAdapter} from '@igp/shared/file';
import {CsvDocumentBuilder, CsvDocumentBuilderAdapter, FileDownloadService, XlsxDocumentBuilder} from '@igp/shared/file';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {MessageLocalizer} from '@igp/shared/localization';
import type {ITracingService} from '@igp/shared/otel';
import {TracingService, TracingServiceSymbol} from '@igp/shared/otel';
import type {IEpicsBuilder} from '@igp/shared/redux';
import type {BaseEpicsBuilder} from '@igp/shared/redux';
import {DownloadEpicsBuilder} from '@igp/shared/redux/download';
import type {IEntityReadService} from '@igp/shared/redux/entity';
import {EntityReadServiceFactorySymbol} from '@igp/shared/redux/entity';
import {MessageEpicsBuilder} from '@igp/shared/redux/message';
import type {IRealtimeService} from '@igp/shared/redux/realtime';
import {RealtimeServiceSymbol} from '@igp/shared/redux/realtime';
import {EntityType} from '@models';
import {RealtimeService} from '@redux';
import type {Factory, ResolutionContext, ServiceIdentifier} from 'inversify';
import {Container} from 'inversify';
import type {UserManagerSettings} from 'oidc-client-ts';

import {ServiceTypes} from '@ioc-container';
import {type Config, getUserManagerSettings} from '@config';
import {AgentLevelReportV2ContainerFacade, agentLevelReportV2ServiceTypes} from '@features/agent-level-report-v2';
import {BoUserContainerFacade, boUserServiceTypes} from '@features/bo-user';
import {CountryLevelPartnerReportContainerFacade, CountryLevelPartnerReportServiceTypes} from '@features/country-level-partner-report';
import {DebtResponsibleMasterAgentContainerFacade, DebtResponsibleMasterAgentsServiceTypes} from '@features/debt-responsible-master-agent';
import {DebtTransferLogContainerFacade, DebtTransferLogServiceTypes} from '@features/debt-transfer-log';
import {KeycloakLogContainerFacade, KeycloakLogServiceTypes} from '@features/keycloak-log';
import {NegativeCarryoverReportContainerFacade, NegativeCarryoverReportServiceTypes} from '@features/negative-carryover-report';
import {P2PPlayerSettingsContainerFacade, p2pPlayerSettingsServiceTypes} from '@features/p2p-player-settings';
import {PayoutReportContainerFacade, PayoutReportServiceTypes} from '@features/payout-report';
import {PlayerEcologyWeeklyContainerFacade, PlayerEcologyWeeklyServiceTypes} from '@features/player-ecology-weekly';
import {PlayerLevelReportV2ContainerFacade, PlayerLevelReportV2ServiceTypes} from '@features/player-level-report-v2';
import {ReferralLinkConfigContainerFacade, ReferralLinkConfigServiceTypes} from '@features/referral-link-config';
import {RepaymentLogContainerFacade, RepaymentLogServiceTypes} from '@features/repayment-log';
import {TransactionContainerFacade} from '@features/transaction';
import {UserProfileContainerFacade} from '@features/user-profile';

import {initAutoMapper} from '../automapper';

export class ContainerBuilder {
    private _inversifyContainer: Container;
    private readonly _config: Config;

    constructor(config: Config) {
        this._config = config;
    }

    public buildContainer(): Container {
        this._inversifyContainer = new Container();
        this.configureAuth();
        this.configureApolloClient();
        this.configureServices();
        this.configureEntities();
        this.configureAutoMapper();
        this.configureEpicBuilders();
        this.configureStrategies();
        this.configureFeatureServices();

        return this._inversifyContainer;
    }

    private configureAuth() {
        const {authUrl, authClientId, authSilentRefreshPage} = this._config;
        const settings = getUserManagerSettings({authUrl, clientId: authClientId, silentRefreshPage: authSilentRefreshPage});
        this._inversifyContainer.bind<UserManagerSettings>(ServiceTypes.UserManagerSettings).toConstantValue(settings);
        this._inversifyContainer.bind<IUserManagerExtended>(ServiceTypes.UserManager).toConstantValue(new UserManagerExtended(settings));
    }

    private configureServices() {
        this._inversifyContainer.bind<IRealtimeService<EntityType>>(RealtimeServiceSymbol).to(RealtimeService);
        this._inversifyContainer.bind<ITracingService>(TracingServiceSymbol).toConstantValue(new TracingService());
        this._inversifyContainer.bind<IMessageLocalizer>(ServiceTypes.MessageLocalizer).toConstantValue(new MessageLocalizer());
        this._inversifyContainer.bind<CsvDocumentBuilder>(ServiceTypes.CsvDocumentBuilder).toConstantValue(new CsvDocumentBuilder());
        this._inversifyContainer.bind<XlsxDocumentBuilder>(ServiceTypes.XlsxDocumentBuilder).toConstantValue(new XlsxDocumentBuilder());
        this._inversifyContainer.bind<IEntityFileAdapter<string>>(ServiceTypes.CsvFileAdapter).to(CsvDocumentBuilderAdapter);
        this._inversifyContainer.bind<FileDownloadService>(ServiceTypes.FileDownloadService).to(FileDownloadService);
        this._inversifyContainer.bind<RestService>(ServiceTypes.RestService).toDynamicValue((context: ResolutionContext) => {
            return new RestXhrService(
                context.get<ITracingService>(TracingServiceSymbol),
                context.get<IUserManagerExtended>(ServiceTypes.UserManager)
            );
        });
        this._inversifyContainer.bind<RestService>(ServiceTypes.RestFetchService).toDynamicValue((context: ResolutionContext) => {
            return new RestFetchApiService(
                context.get<ITracingService>(TracingServiceSymbol),
                context.get<IUserManagerExtended>(ServiceTypes.UserManager)
            );
        });
    }

    private configureEntities() {
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(boUserGroupServiceTypes.service)
            .toDynamicValue(
                (context: ResolutionContext) =>
                    new BoUserGroupService(
                        context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                        context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                    )
            );
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.UserProfileService)
            .toDynamicValue(
                (context: ResolutionContext) =>
                    new UserProfileService(
                        context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                        context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer),
                        context.get<Mapper>(ServiceTypes.AutoMapper)
                    )
            );
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.TransactionService)
            .toDynamicValue(
                (context: ResolutionContext) =>
                    new TransactionService(
                        context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                        context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                    )
            );
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.CountryService)
            .toDynamicValue((context: ResolutionContext) => {
                return new CountriesService<EntityType>(
                    context.get<RestService>(ServiceTypes.RestFetchService),
                    this._config.countriesApiUrl
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.BulkOperationService)
            .toDynamicValue((context: ResolutionContext) => {
                return new BulkOperationService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.AgentProfileService)
            .toDynamicValue((context: ResolutionContext) => {
                return new AgentProfileService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.UserActivitySummaryService)
            .toDynamicValue((context: ResolutionContext) => {
                return new UserActivitySummaryService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.PlayerBonusService)
            .toDynamicValue((context: ResolutionContext) => {
                return new PlayerBonusService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.BonusCodeService)
            .toDynamicValue((context: ResolutionContext) => {
                return new BonusCodeService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.BackpackService)
            .toDynamicValue((context: ResolutionContext) => {
                return new BackpackService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEntityReadService<EntityType>>(ServiceTypes.PlayerReferralService)
            .toDynamicValue((context: ResolutionContext) => {
                return new PlayerReferralService(
                    context.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP),
                    context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer)
                );
            });
        this._inversifyContainer
            .bind<IEpicsBuilder>(ServiceTypes.PlayerReferralEpicsBuidler)
            .toDynamicValue(
                (context: ResolutionContext) =>
                    new PlayerReferralEpicsBuilder(
                        context.get<Mapper>(ServiceTypes.AutoMapper),
                        context.get<IPlayerReferralUpdateService>(ServiceTypes.PlayerReferralService),
                        context.get<IUserProfileService>(ServiceTypes.UserProfileService)
                    )
            );
        this._inversifyContainer
            .bind<Factory<IEntityReadService<EntityType>, [EntityType]>>(EntityReadServiceFactorySymbol)
            .toFactory(() => (entityType: EntityType) => {
                const serviceMap: Record<EntityType, IEntityReadService<EntityType>> = {
                    BoUser: this.getService<IEntityReadService<EntityType>>(boUserServiceTypes.service),
                    BoUserGroup: this.getService<IEntityReadService<EntityType>>(boUserGroupServiceTypes.service),
                    UserProfile: this.getService<IEntityReadService<EntityType>>(ServiceTypes.UserProfileService),
                    Transaction: this.getService<IEntityReadService<EntityType>>(ServiceTypes.TransactionService),
                    AgentLevelReportV2: this.getService<IEntityReadService<EntityType>>(agentLevelReportV2ServiceTypes.service),
                    PlayerLevelReportV2: this.getService<IEntityReadService<EntityType>>(
                        PlayerLevelReportV2ServiceTypes.PlayerLevelReportV2Service
                    ),
                    Country: this.getService<IEntityReadService<EntityType>>(ServiceTypes.CountryService),
                    BulkOperation: this.getService<IEntityReadService<EntityType>>(ServiceTypes.BulkOperationService),
                    AgentProfile: this.getService<IEntityReadService<EntityType>>(ServiceTypes.AgentProfileService),
                    NegaitveCarryoverReport: this.getService<IEntityReadService<EntityType>>(NegativeCarryoverReportServiceTypes.Service),
                    NegativeCarryoverWeeklySummary: this.getService<IEntityReadService<EntityType>>(
                        NegativeCarryoverReportServiceTypes.WeeklySummaryService
                    ),
                    UserActivitySummary: this.getService<IEntityReadService<EntityType>>(ServiceTypes.UserActivitySummaryService),
                    PlayerBonus: this.getService<IEntityReadService<EntityType>>(ServiceTypes.PlayerBonusService),
                    BonusCode: this.getService<IEntityReadService<EntityType>>(ServiceTypes.BonusCodeService),
                    Backpack: this.getService<IEntityReadService<EntityType>>(ServiceTypes.BackpackService),
                    PlayerReferral: this.getService<IEntityReadService<EntityType>>(ServiceTypes.PlayerReferralService),
                    CountryLevelPartnerReport: this.getService<IEntityReadService<EntityType>>(
                        CountryLevelPartnerReportServiceTypes.service
                    ),
                    DebtResponsibleMasterAgents: this.getService<IEntityReadService<EntityType>>(
                        DebtResponsibleMasterAgentsServiceTypes.service
                    ),
                    DebtTransferLog: this.getService<IEntityReadService<EntityType>>(DebtTransferLogServiceTypes.service),
                    RepaymentLog: this.getService<IEntityReadService<EntityType>>(RepaymentLogServiceTypes.service),
                    PayoutReport: this.getService<IEntityReadService<EntityType>>(PayoutReportServiceTypes.service),
                    P2PPlayerSettings: this.getService<IEntityReadService<EntityType>>(p2pPlayerSettingsServiceTypes.service),
                    PlayerEcologyWeekly: this.getService<IEntityReadService<EntityType>>(PlayerEcologyWeeklyServiceTypes.service),
                    KeycloakLog: this.getService<IEntityReadService<EntityType>>(KeycloakLogServiceTypes.service),
                    ReferralLinkConfig: this.getService<IEntityReadService<EntityType>>(ReferralLinkConfigServiceTypes.service),
                };
                return serviceMap[entityType];
            });
    }

    private configureAutoMapper() {
        const mapper = initAutoMapper();

        this._inversifyContainer.bind<Mapper>(ServiceTypes.AutoMapper).toConstantValue(mapper);
    }

    private configureApolloClient() {
        this._inversifyContainer
            .bind<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP)
            .toDynamicValue((context: ResolutionContext) => {
                const {igpGraphQlHttpUrl, igpGraphQLWsUrl, igpGraphQlApiKey, igpGraphQlAuthType} = this._config;
                let builder: IApolloClientBuilder = new ApolloClientGraphQlTransportWsBuilder(
                    context.get<ITracingService>(TracingServiceSymbol),
                    context.get<IUserManagerExtended>(ServiceTypes.UserManager)
                );
                builder = builder.withHttp(igpGraphQlHttpUrl).withAuth(igpGraphQlAuthType, {apiKey: igpGraphQlApiKey});

                builder = builder.withWebsocket(igpGraphQLWsUrl);
                return builder.buildClient();
            })
            .inSingletonScope();

        const inMemoryCache = new ApolloClientInMemoryCache();

        this._inversifyContainer
            .bind<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGPMocked)
            .toDynamicValue((context: ResolutionContext) => {
                const {igpGraphQlHttpUrl, igpGraphQLWsUrl, igpGraphQlApiKey, igpGraphQlAuthType} = this._config;
                let builder: IApolloClientBuilder = new ApolloClientGraphQlTransportWsBuilder(
                    context.get<ITracingService>(TracingServiceSymbol),
                    context.get<IUserManagerExtended>(ServiceTypes.UserManager)
                );
                const {env} = this._config;
                const cache = env === 'DEV' ? inMemoryCache.init() : null;

                builder = builder
                    .withHttp(igpGraphQlHttpUrl)
                    .withAuth(igpGraphQlAuthType, {apiKey: igpGraphQlApiKey})
                    .withClientMocking(cache, []);

                builder = builder.withWebsocket(igpGraphQLWsUrl);
                return builder.buildClient();
            })
            .inSingletonScope();
    }

    private configureStrategies() {
        this._inversifyContainer
            .bind<LoadP2PTransferStrategy<EntityType>>(ServiceTypes.LoadP2PTransferStrategy)
            .toDynamicValue((context: ResolutionContext) => {
                return new LoadP2PTransferStrategy(
                    context.get<IEntityReadService<EntityType>>(ServiceTypes.UserProfileService),
                    context.get<Mapper>(ServiceTypes.AutoMapper),
                    EntityType.UserProfile
                );
            });
        this._inversifyContainer
            .bind<LoadP2PTransferViaFileStrategy<EntityType>>(ServiceTypes.LoadP2PTransferViaFileStrategy)
            .toDynamicValue((context: ResolutionContext) => {
                return new LoadP2PTransferViaFileStrategy(
                    context.get<IEntityReadService<EntityType>>(ServiceTypes.UserProfileService),
                    context.get<Mapper>(ServiceTypes.AutoMapper),
                    EntityType.UserProfile,
                    new P2PTransferTemplateFactory(
                        context.get<XlsxDocumentBuilder>(ServiceTypes.XlsxDocumentBuilder),
                        context.get<CsvDocumentBuilder>(ServiceTypes.CsvDocumentBuilder)
                    )
                );
            });
        this._inversifyContainer
            .bind<IBulkApplyStrategy<ApplyP2PTransfersRequest>>(ServiceTypes.ApplyP2PTransfersStrategy)
            .toConstantValue(new ApplyP2PTransfersStrategy());
        this._inversifyContainer
            .bind<IBulkStrategy<ValidationP2PTransferRequest, ValidationStrategyResponse>>(ServiceTypes.ValidationP2PTransferStrategy)
            .toDynamicValue((context: ResolutionContext) => {
                return new ValidationP2PTransferStrategy(
                    context.get<IEntityReadService<EntityType>>(ServiceTypes.UserProfileService),
                    EntityType.UserProfile
                );
            });
        this._inversifyContainer
            .bind<
                IBulkStrategy<PerformStrategyRequest<P2PTransferModel>, PerformStrategyOperationResponse>
            >(ServiceTypes.PerformP2PTransfersStrategy)
            .toDynamicValue((context: ResolutionContext) => {
                return new PerformP2PTransfersStrategy(context.get<ITransactionService>(ServiceTypes.TransactionService));
            });
    }

    private getService<IService>(serviceKey: ServiceIdentifier) {
        return this._inversifyContainer.isBound(serviceKey) ? this._inversifyContainer.get<IService>(serviceKey) : null;
    }

    private configureFeatureServices() {
        const facades = [
            new UserProfileContainerFacade(this._inversifyContainer),
            new TransactionContainerFacade(this._inversifyContainer),
            new BoUserContainerFacade(this._inversifyContainer),
            new AgentLevelReportV2ContainerFacade(this._inversifyContainer),
            new PlayerLevelReportV2ContainerFacade(this._inversifyContainer),
            new NegativeCarryoverReportContainerFacade(this._inversifyContainer),
            new CountryLevelPartnerReportContainerFacade(this._inversifyContainer),
            new DebtResponsibleMasterAgentContainerFacade(this._inversifyContainer),
            new DebtTransferLogContainerFacade(this._inversifyContainer),
            new RepaymentLogContainerFacade(this._inversifyContainer),
            new PayoutReportContainerFacade(this._inversifyContainer),
            new P2PPlayerSettingsContainerFacade(this._inversifyContainer),
            new PlayerEcologyWeeklyContainerFacade(this._inversifyContainer),
            new KeycloakLogContainerFacade(this._inversifyContainer),
            new ReferralLinkConfigContainerFacade(this._inversifyContainer),
        ];
        facades.forEach(d => d.bindAll());
    }

    private configureEpicBuilders() {
        this._inversifyContainer
            .bind<IEpicsBuilder>(ServiceTypes.UserProfileEpicsBuilder)
            .toDynamicValue(
                (context: ResolutionContext) =>
                    new UserProfileEpicsBuilder(
                        context.get<UserProfileService<CustomErrorCodesLocalizationKeys>>(ServiceTypes.UserProfileService),
                        context.get<CsvDocumentBuilderAdapter<UserProfileViewModelKeys>>(ServiceTypes.CsvFileAdapter),
                        context.get<Mapper>(ServiceTypes.AutoMapper)
                    )
            );
        this._inversifyContainer
            .bind<BaseEpicsBuilder>(ServiceTypes.DonwloadEpicsBuilder)
            .toDynamicValue(
                (context: ResolutionContext) =>
                    new DownloadEpicsBuilder(
                        context.get<CsvDocumentBuilderAdapter<string>>(ServiceTypes.CsvFileAdapter),
                        this._config.igpGraphQlHttpUrl
                    )
            );
        this._inversifyContainer
            .bind<BaseEpicsBuilder>(ServiceTypes.MessageEpicsBuilder)
            .toDynamicValue(
                (context: ResolutionContext) => new MessageEpicsBuilder(context.get<IMessageLocalizer>(ServiceTypes.MessageLocalizer))
            );
    }
}
